import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { isUserAdmin } from '../utils/adminUtils';
import {
  adjustClientBalanceManually,
  buildClientBalancesFromEntries,
  formatClientBalance,
  listAllInvoicesForMigration,
  subscribeToAccountingEntries
} from '../utils/accountingUtils';

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function AdminKlientuBalansasPage() {
  const { currentUser } = useAuth();
  const isAdminUser = isUserAdmin(currentUser);

  const [entries, setEntries] = useState([]);
  const [invoiceClients, setInvoiceClients] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [adjustClient, setAdjustClient] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustDate, setAdjustDate] = useState(todayIso());
  const [adjustNote, setAdjustNote] = useState('');
  const [adjustSaving, setAdjustSaving] = useState(false);

  useEffect(() => {
    if (!isAdminUser) return undefined;
    return subscribeToAccountingEntries(setEntries);
  }, [isAdminUser]);

  useEffect(() => {
    if (!isAdminUser) return;
    let cancelled = false;
    (async () => {
      try {
        const invoices = await listAllInvoicesForMigration({ fromYear: 2024 });
        if (!cancelled) setInvoiceClients(invoices);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Nepavyko užkrauti klientų iš SF.');
      }
    })();
    return () => { cancelled = true; };
  }, [isAdminUser]);

  const balances = useMemo(
    () => buildClientBalancesFromEntries(entries, { includeZeroFromInvoices: invoiceClients }),
    [entries, invoiceClients]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return balances;
    return balances.filter((b) => (
      String(b.clientName || '').toLowerCase().includes(q)
      || String(b.clientEmail || '').toLowerCase().includes(q)
    ));
  }, [balances, search]);

  const openAdjust = (client) => {
    setAdjustClient(client);
    setAdjustAmount('');
    setAdjustDate(todayIso());
    setAdjustNote('');
    setError('');
  };

  const closeAdjust = () => {
    if (adjustSaving) return;
    setAdjustClient(null);
  };

  const handleAdjustSave = async () => {
    if (!adjustClient) return;
    setAdjustSaving(true);
    setError('');
    setSuccess('');
    try {
      await adjustClientBalanceManually({
        clientId: adjustClient.clientId,
        clientEmail: adjustClient.clientEmail,
        clientName: adjustClient.clientName,
        amountEur: adjustAmount,
        date: adjustDate,
        note: adjustNote
      });
      const n = Number(adjustAmount);
      setSuccess(
        n > 0
          ? `Balansas padidintas ${n.toFixed(2)} Eur (${adjustClient.clientName || adjustClient.clientEmail}).`
          : `Balansas sumažintas ${Math.abs(n).toFixed(2)} Eur (${adjustClient.clientName || adjustClient.clientEmail}).`
      );
      setAdjustClient(null);
    } catch (err) {
      setError(err.message || 'Nepavyko pakoreguoti balanso.');
    } finally {
      setAdjustSaving(false);
    }
  };

  const canSaveAdjust = Boolean(
    adjustClient
    && Number(adjustAmount) !== 0
    && Number.isFinite(Number(adjustAmount))
    && /^\d{4}-\d{2}-\d{2}$/.test(adjustDate)
  );

  if (!currentUser || !isAdminUser) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Tik administratorius gali pasiekti klientų balansą.
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="md">
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 700, letterSpacing: '-0.02em' }}>
          Klientų balansas
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          Klientai, kuriems buvo išrašyta SF programoje, ir jų likutis pagal apskaitos žurnalą.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <strong>+Eur</strong> — jūs skolingi klientui (gauti pinigai − suteiktų paslaugų SF).{' '}
          <strong>−Eur</strong> — klientas skolingas jums.{' '}
          Mygtukas <strong>+</strong> — rankinė korekcija (teigiama suma didina, neigiama mažina).
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <TextField
              label="Ieškoti"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              placeholder="Vardas arba el. paštas"
            />
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {filtered.length} klientų
            </Typography>
          </Stack>
        </Paper>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Klientas</TableCell>
                <TableCell>El. paštas</TableCell>
                <TableCell align="right">Balansas</TableCell>
                <TableCell align="right" width={56} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      Nėra klientų. Išrašykite SF arba pridėkite įrašų Apskaitoje.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((b) => {
                const bal = Number(b.balance || 0);
                const color = bal > 0 ? 'success.main' : bal < 0 ? 'error.main' : 'text.primary';
                return (
                  <TableRow key={b.clientKey} hover>
                    <TableCell>{b.clientName || '—'}</TableCell>
                    <TableCell>{b.clientEmail || '—'}</TableCell>
                    <TableCell align="right">
                      <Typography component="span" sx={{ fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>
                        {formatClientBalance(bal)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="success"
                        aria-label="Koreguoti balansą"
                        onClick={() => openAdjust(b)}
                        title="+ pinigai / korekcija"
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      <Dialog open={Boolean(adjustClient)} onClose={closeAdjust} fullWidth maxWidth="xs">
        <DialogTitle>Koreguoti balansą</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {adjustClient && (
                <>
                  <strong>{adjustClient.clientName || 'Klientas'}</strong>
                  {adjustClient.clientEmail ? ` (${adjustClient.clientEmail})` : ''}
                  {' · dabar '}
                  {formatClientBalance(adjustClient.balance)}
                </>
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Teigiama suma (pvz. 100) — didina balansą (gauti pinigai). Neigiama (pvz. −40) — mažina.
            </Typography>
            <TextField
              label="Suma Eur"
              type="number"
              size="small"
              value={adjustAmount}
              onChange={(e) => setAdjustAmount(e.target.value)}
              fullWidth
              inputProps={{ step: '0.01' }}
              disabled={adjustSaving}
              autoFocus
              helperText="Galima įvesti su minusu"
            />
            <TextField
              label="Data"
              type="date"
              size="small"
              value={adjustDate}
              onChange={(e) => setAdjustDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled={adjustSaving}
            />
            <TextField
              label="Pastaba (neprivaloma)"
              size="small"
              value={adjustNote}
              onChange={(e) => setAdjustNote(e.target.value)}
              fullWidth
              disabled={adjustSaving}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAdjust} disabled={adjustSaving}>Atšaukti</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleAdjustSave}
            disabled={!canSaveAdjust || adjustSaving}
          >
            {adjustSaving ? 'Saugoma…' : 'Įrašyti'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
